/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */

/**
 * Dynamic line items table that renders per-supplier columns.
 * If no columns config is provided, falls back to the default 8-column layout.
 */

const CURRENCY_COLS = new Set(['UNIT_PRICE', 'LINE_TOTAL'])

function getItemValue(item, columnKey, index) {
  switch (columnKey) {
    case 'LINE_NUMBER': return item.line_number || (index + 1)
    case 'ITEM_CODE': return item.item_code || ''
    case 'DESCRIPTION': return item.description || ''
    case 'HEIGHT': return item.height_z ? `${item.height_z}` : ''
    case 'WIDTH': return item.width_y ? `${item.width_y}` : ''
    case 'LENGTH': return item.length_x ? `${item.length_x}` : ''
    case 'COLOR': return item.color || ''
    case 'GLASS_TYPE': return item.glass_type || ''
    case 'GLASS_COLOUR': return item.glass_colour || ''
    case 'QUANTITY': return item.quantity || 0
    case 'UOM': return item.uom || 'EACH'
    case 'UNIT_PRICE': return parseFloat(item.unit_price || 0)
    case 'LINE_TOTAL': return parseFloat(item.line_total || (item.quantity * item.unit_price) || 0)
    default: return ''
  }
}

const DEFAULT_COLUMNS = [
  { column_key: 'LINE_NUMBER', display_name: '#', align: 'left' },
  { column_key: 'ITEM_CODE', display_name: 'Item Code', align: 'left' },
  { column_key: 'DESCRIPTION', display_name: 'Description', align: 'left' },
  { column_key: 'HEIGHT', display_name: 'Height', align: 'right' },
  { column_key: 'WIDTH', display_name: 'Width', align: 'right' },
  { column_key: 'LENGTH', display_name: 'Length', align: 'right' },
  { column_key: 'QUANTITY', display_name: 'Qty', align: 'right' },
  { column_key: 'UOM', display_name: 'UOM', align: 'center' },
  { column_key: 'UNIT_PRICE', display_name: 'Unit Price', align: 'right' },
  { column_key: 'LINE_TOTAL', display_name: 'Line Total', align: 'right' }
]

function POLineItemsTable({ lineItems, totalAmount, columns }) {
  const cols = (columns && columns.length > 0) ? columns : DEFAULT_COLUMNS

  // Find the LINE_TOTAL column index for the footer colspan
  const totalColIndex = cols.findIndex(c => c.column_key === 'LINE_TOTAL')
  const footerColspan = totalColIndex > 0 ? totalColIndex : cols.length - 1

  return (
    <div className="card">
      <div className="card-header">
        <h2>Line Items ({lineItems?.length || 0})</h2>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {cols.map(col => (
                <th key={col.column_key} style={{ textAlign: col.align || 'left' }}>
                  {col.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lineItems?.map((item, index) => (
              <tr key={item.id}>
                {cols.map(col => {
                  const val = getItemValue(item, col.column_key, index)
                  const isCurrency = CURRENCY_COLS.has(col.column_key)
                  const isBold = col.column_key === 'ITEM_CODE' || col.column_key === 'LINE_TOTAL'

                  return (
                    <td
                      key={col.column_key}
                      style={{
                        textAlign: col.align || 'left',
                        fontWeight: isBold ? 500 : 'normal',
                        fontSize: col.column_key === 'DESCRIPTION' ? undefined : '0.9rem',
                        color: ['HEIGHT', 'WIDTH', 'LENGTH', 'COLOR', 'GLASS_TYPE', 'GLASS_COLOUR'].includes(col.column_key) ? '#666' : undefined
                      }}
                    >
                      {isCurrency ? `$${parseFloat(val || 0).toFixed(2)}` : (val || '-')}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={footerColspan} style={{ textAlign: 'right', fontWeight: 600 }}>Total:</td>
              <td style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                ${parseFloat(totalAmount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
              </td>
              {totalColIndex < cols.length - 1 && (
                <td colSpan={cols.length - totalColIndex - 1}></td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

export default POLineItemsTable
